import React, { useState, useEffect, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { formatDateDD_MM_YY } from "../helpers/datetimeHelpers";
import { getTrainerSchedules } from "../services/trainersService";
import { getTrainerAvailabilitiesByTrainerPublicId } from "../services/trainerAvailabilities";
import { getCurrentUserFromLocalStorage, getI18nLanguageFromLocalStorage } from "../helpers/localStorageHelper";

const AvailabilityTable = ({
  trainerPublicId,
  translations = {},
  setBookingDate,
  labels,
  wallet = { usedTicket: 0, remainingTicket: 0 },
  setVisible,
  showErrorToast,
  bookedCell
}) => {
  // The tutor-detail modal renders this table read-only and does NOT pass `labels`.
  // Fall back to the modal's available/unavailable translations so the schedule cells
  // still show text instead of crashing on `labels.*` (which would unmount the section).
  const lbls = labels ?? {
    reservationAvailableLabel: translations.availableLabel,
    reservationClosedLabel: translations.unavailableLabel,
    fullyBookedLabel: translations.unavailableLabel,
  };
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(today);
    monday.setDate(diff);
    return monday;
  });

  const days = useMemo(() => [
    { field: "day0", header: translations.Monday },
    { field: "day1", header: translations.Tuesday },
    { field: "day2", header: translations.Wednesday },
    { field: "day3", header: translations.Thursday },
    { field: "day4", header: translations.Friday },
    { field: "day5", header: translations.Saturday },
    { field: "day6", header: translations.Sunday },
  ], [translations]);

  const [tableData, setTableData] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedCell, setSelectedCell] = useState(null);
  const [trainerSchedules, setTrainerSchedules] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const trainerAvailabilityApiResponse = await getTrainerAvailabilitiesByTrainerPublicId(trainerPublicId);
        const parsedTimes = JSON.parse(trainerAvailabilityApiResponse.availableTimes);
        setAvailableTimes(parsedTimes);
      } catch (error) {
        setAvailableTimes([]);
      }

      try {
        const trainerScheduleApiResponse = await getTrainerSchedules(trainerPublicId);
        setTrainerSchedules(trainerScheduleApiResponse);
      } catch (error) {
        setTrainerSchedules([]);
      }
    };
    fetchData();
    setSelectedCell(null);
  }, [trainerPublicId]);

  // Update local state when bookedCell is provided
  useEffect(() => {
    if (bookedCell && bookedCell.timestamp) {
      setTrainerSchedules(prevSchedules => {
        // Check if this schedule already exists
        const existingSchedule = prevSchedules.find(schedule => schedule.startTime === bookedCell.timestamp);
        if (!existingSchedule) {
          // Add the new booking to local schedules
          return [...prevSchedules, { startTime: bookedCell.timestamp }];
        }
        return prevSchedules;
      });
      setSelectedCell(null);
    }
  }, [bookedCell]);

  useEffect(() => {
    const currentTimestamp = Math.floor(Date.now() / 1000); // Current time in seconds
    const availabilityTimeInSeconds = process.env.trainerAvailability.availabilityTimeInMinutes;
    const minAvailableTimestamp = currentTimestamp + availabilityTimeInSeconds; // Minimum timestamp for availability (based on environment)
    const LECTURE_DURATION = 30; // 30 minutes per slot

    const rows = [];
    for (let hour = 0; hour < 24; hour++) {
      for (const minute of [0, LECTURE_DURATION]) {
        // Format time label like Angular component: hour:00 - hour:30, hour:30 - (hour+1):00
        const startTime = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
        let endHour = hour;
        let endMinute = minute + LECTURE_DURATION;
        if (endMinute >= 60) {
          endHour += 1;
          endMinute -= 60;
        }
        if (endHour === 24) {
          endHour = 0;
        }
        const endTime = `${String(endHour).padStart(2, "0")}:${String(endMinute).padStart(2, "0")}`;
        const timeStr = `${startTime} - ${endTime}`;

        const row = { time: timeStr };
        for (let day = 0; day < 7; day++) {
          const date = new Date(weekStart);
          date.setDate(weekStart.getDate() + day);
          date.setHours(hour, minute, 0, 0);
          const timestamp = Math.floor(date.getTime() / 1000);

          // Check if the time is available
          const isAvailable = availableTimes?.some((t) => t.time === timestamp) ?? false;
          // Check if there's a schedule at this time
          const hasSchedule = trainerSchedules?.some(
            (schedule) => schedule.startTime === timestamp
          ) ?? false;

          // Init state
          let isFree = false;
          let label = "";
          let cssClass = "grayed-out";
          let isDisabled = false;

          if (timestamp < currentTimestamp) {
            cssClass = "grayed-out";
          }
          else if (hasSchedule) {
            isFree = false;
            label = lbls.fullyBookedLabel;
            cssClass = "text-danger-txt";
          }
          else if (timestamp <= minAvailableTimestamp) {
            // From now to (now + environment-based time), follow isAvailable
            if (isAvailable) {
              isFree = true;
              label = lbls.reservationAvailableLabel;
              cssClass = "text-default-txt";
              isDisabled = true;
            }
          } else {
            // Beyond environment-based time, follow isAvailable logic
            if (isAvailable) {
              isFree = true;
              label = lbls.reservationAvailableLabel;
              cssClass = "text-default-txt";
            }
          }

          row[`day${day}`] = {
            text: label,
            class: cssClass,
            timestamp: timestamp,
            isFree,
            isDisabled,
            key: `${day}-${timeStr}`,
            bookingDate: `${formatDateDD_MM_YY(new Date(timestamp * 1000))} (${days.find(d => d.field === `day${day}`).header})`,
            bookingTime: timeStr,
            wallet: `${wallet.usedTicket}/${wallet.usedTicket + wallet.remainingTicket}`
          };
        }
        rows.push(row);
      }
    }
    setTableData(rows);
  }, [weekStart, availableTimes, trainerSchedules, lbls.reservationAvailableLabel, lbls.reservationClosedLabel, days, wallet]);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  const goToPreviousWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(weekStart.getDate() - 7);
    setWeekStart(newStart);
    setSelectedCell(null);
  };

  const goToNextWeek = () => {
    const newStart = new Date(weekStart);
    newStart.setDate(weekStart.getDate() + 7);
    setWeekStart(newStart);
    setSelectedCell(null);
  };

  const handleCellClick = (data) => {
    setSelectedCell(data.key);
    if (typeof setBookingDate === "function") setBookingDate(data);
    if (typeof setVisible === "function") setVisible(true);
  };

  const cellBodyTemplate = (rowData, { field }) => (
    <div
      className={`
        cell-content 
        ${rowData[field].class.toLowerCase()}
        ${rowData[field].isFree && !rowData[field].isDisabled > 0 ? "cursor-pointer" : "cursor-auto"}
        ${rowData[field].isDisabled ? "disabled" : ""}
        ${cellClassName(rowData, { field })}
        keduall-typography text-14-normal-400
      `}
      // onClick={() => {
      //   const currentUser = getCurrentUserFromLocalStorage();
      //   if (!currentUser) {
      //     const lang = getI18nLanguageFromLocalStorage();
      //     window.location.href = `/auth/login?lang=${lang}&returnUrl=${window.location.pathname}`;
      //     return;
      //   }
      //   if (rowData[field].isFree && !rowData[field].isDisabled && wallet.remainingTicket > 0) {
      //     handleCellClick(rowData[field]);
      //   } else if (rowData[field].isFree && !rowData[field].isDisabled && wallet.remainingTicket === 0 && typeof showErrorToast === "function") {
      //     showErrorToast();
      //   }
      // }}
    >
      {rowData[field].text}
    </div>
  );

  const cellClassName = (rowData, { field }) => rowData[field].key === selectedCell ? "selected" : "";

  const header = (
    <div className="flex justify-content-between align-items-center">
      <Button
        onClick={goToPreviousWeek}
        className="keduall-buttons button-filled-default-small-primary"
      >
        <i className="pi pi-chevron-left" />
      </Button>
      <span className="keduall-typography text-16-semibold-600 text-default-txt">{`${formatDateDD_MM_YY(weekStart)} - ${formatDateDD_MM_YY(weekEnd)}`}</span>
      <Button
        onClick={goToNextWeek}
        className="keduall-buttons button-filled-default-small-primary"
      >
        <i className="pi pi-chevron-right" />
      </Button>
    </div>
  );

  useEffect(() => {
  const wrapper = document.querySelector(".p-datatable .p-datatable-wrapper");
    if (wrapper) {
      wrapper.classList.add("keduall-scrollbar");
    }
  }, []);

  return (
    <div className="w-full">
      <DataTable value={tableData} header={header} className="availability-table keduall-scrollbar flex flex-column gap-3" scrollable="true">
        <Column field="time" header={translations.time} frozen className="keduall-typography text-default-txt text-14-normal-400" />
        {days.map((day) => (
          <Column
            key={day.field}
            field={day.field}
            header={day.header}
            body={cellBodyTemplate}
          />
        ))}
      </DataTable>
    </div>
  );
};

export default AvailabilityTable;